import {
  addToast,
  Button,
  Inputß,
  Selectß,
  Toastß,
} from '@otter-ai/den-components';
import { InformationIcon } from '@otter-ai/den-icons';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { SECOND_MS } from '@/constants/time';
import { descriptionFormatRules } from '@/features/account-settings/shared/utils/text';
import {
  SETUP_TWO_FACTOR_AUTH_SMS_METHOD_STATUS,
  useSetupTwoFactorAuthSmsMethod,
} from '@/query/mutations/useSetupTwoFactorAuthSmsMethodMutation';
import { useGetUsersTwoFactorAuthMethod } from '@/shared/hooks/two-factor-authentication/useGetUsersTwoFactorAuthMethod';
import {
  useVerifyAccount,
  type Verified,
} from '@/shared/hooks/verifyAccount/useVerifyAccount';
import { Security2FAType } from '@/shared/types/auth';
import {
  US_CA_INTERNATIONAL_PHONE_NUMBER_LENGTH,
  US_CA_INTERNATIONAL_PHONE_NUMBER_PREFIX,
} from '../constants';
import { StateEvent } from '../state-machine-config';
import {
  formatNationalUsPhoneNumber,
  internationalUsPhoneNumber,
} from '../utils';
import { VerifyCodeForm } from './VerifyCodeForm';

/**
 * The state when the user is setting up two-factor authentication via SMS text message.
 */
export function SmsSetupState(props: {
  [StateEvent.PICK_DIFFERENT_METHOD]: () => void;
  [StateEvent.FINISH_SETUP]: () => void;
}) {
  const t = useTranslations('settings:security.twoFactorAuthStates.smsSetup');
  const [disableForm, setDisableForm] = useState(false);
  const delayBeforeRetryingMs = useRef(0.5 * SECOND_MS);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const { openVerifyAccount } = useVerifyAccount();
  const phoneNumberForm = useForm<{ phoneNumber: string }>({
    disabled: disableForm,
  });
  const { enableSmsTwoFactorAuth } = useSetupTwoFactorAuthSmsMethod();
  const { twoFactorAuthMethod } = useGetUsersTwoFactorAuthMethod();

  const onSubmit = async (data: { phoneNumber: string }) => {
    setDisableForm(true);

    // If 2FA is already enabled, we need to verify the user before enabling SMS 2FA.
    let verifiedStatus: Verified | null = null;
    if (twoFactorAuthMethod !== Security2FAType.NONE) {
      const verified = await openVerifyAccount();
      if (verified === false) {
        return;
      }
      verifiedStatus = verified;
    }

    const smsStatus = await enableSmsTwoFactorAuth({
      phoneNumber: data.phoneNumber,
      verificationMethod: verifiedStatus?.method ?? null,
      verificationToken: verifiedStatus?.token ?? null,
    });

    if (
      smsStatus === SETUP_TWO_FACTOR_AUTH_SMS_METHOD_STATUS.CHECK_SMS_TO_VERIFY
    ) {
      phoneNumberForm.reset({ phoneNumber: '' });
      phoneNumberForm.trigger();
      addToast(
        <Toastß
          icon={<InformationIcon />}
          description={t('checkYourPhoneForVerificationCode')}
        />,
      );
      codeInputRef.current?.focus();
      codeInputRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    setTimeout(() => {
      setDisableForm(false);
      delayBeforeRetryingMs.current = nextDelayMs(
        delayBeforeRetryingMs.current,
      );
    }, delayBeforeRetryingMs.current);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grow font-semibold py-4">
        {t.rich('viaSmsTextMessage', {
          ...descriptionFormatRules({}),
          where: (child) => <span className="font-normal">{child}</span>,
        })}
      </div>
      <ol className="list-decimal pl-[1.5em] [&>li]:list-item flex flex-col gap-4">
        <li className="flex flex-col gap-4">
          <div className="mb-4">
            {t('enterTheMobilePhoneNumberToReceiveYourVerificationCodeViaSms')}
          </div>
          <div className="flex flex-col gap-4 max-w-[320px]">
            <form
              onSubmit={phoneNumberForm.handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
            >
              <Selectß
                value={US_CA_INTERNATIONAL_PHONE_NUMBER_PREFIX}
                options={[
                  {
                    label: t('unitedStatesAndCanada'),
                    value: US_CA_INTERNATIONAL_PHONE_NUMBER_PREFIX,
                  },
                ]}
                onChange={() => {}} // No action as it is always +1
                placeholder="Select country code"
                variant="outlineNeutral"
                version="legacy"
                className="w-fit min-w-[16em] max-w-[20em]"
              />
              <Inputß
                as="text"
                placeholder={t('enterPhoneNumberPlaceholder')}
                inputMode="tel"
                className="w-fit min-w-[16em] max-w-[20em]"
                {...phoneNumberForm.register('phoneNumber', {
                  setValueAs: internationalUsPhoneNumber,
                  onChange: (event) => {
                    event.stopPropagation();
                    event.target.value = formatNationalUsPhoneNumber(
                      event.target.value,
                    );
                  },
                  maxLength: US_CA_INTERNATIONAL_PHONE_NUMBER_LENGTH,
                  minLength: US_CA_INTERNATIONAL_PHONE_NUMBER_LENGTH,
                  required: true,
                })}
                data-testid="2fa-setup-sms-phone-number-input-field"
              />
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  variant="outlineNeutral"
                  version="legacy"
                  onClick={props[StateEvent.PICK_DIFFERENT_METHOD]}
                  className="w-full sm:w-auto"
                >
                  {t('cancelButton')}
                </Button>
                <Button
                  type="submit"
                  disabled={!phoneNumberForm.formState.isValid || disableForm}
                  className="w-full sm:w-auto"
                  data-testid="2fa-setup-sms-text-me-a-code-button"
                >
                  {t('textMeACode')}
                </Button>
              </div>
            </form>
          </div>
        </li>
        <li>
          <VerifyCodeForm
            twoFactorAuthType={Security2FAType.SMS}
            onFinishSetup={props[StateEvent.FINISH_SETUP]}
            onPickDifferentMethod={props[StateEvent.PICK_DIFFERENT_METHOD]}
          />
        </li>
      </ol>
    </div>
  );
}

function nextDelayMs(delayMs: number) {
  return delayMs * 2;
}
